import cn from 'classnames';
import { graphql, useFragment } from 'react-relay';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient.ts';
import { type FC, useRef, useState, useEffect, useCallback } from 'react';
import scrollIntoView from 'scroll-into-view';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import { LABEL_VALUE_MONITORING_GROUP_PDP } from 'dibs-tracking/src/prometheus/peformanceTracking.ts';
import { usePdpRespMarketplaceLayoutPrimaryContext } from '../PdpRespMarketplaceLayout/PdpRespMarketplaceLayoutPrimaryContextProvider.tsx';
import { usePdpSharedSimilarSoldItemsContext } from '../../PdpSharedSimilarSoldItems/PdpSharedSimilarSoldItemsContext/PdpSharedSimilarSoldItemsContext.tsx';
import { trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2.ts';
import { showPricingGuidance as showPricingGuidanceHelper } from 'dibs-pricing-guidance/exports/showPricingGuidance.ts';
import {
    PDP_ESTIMATE_SIMILAR_SOLD_TEST_NAME,
    PDP_ITEM_MODULE_TEST_NAME,
    PDP_SIMILAR_SOLD_ITEMS_MODULE_TEST_NAME,
} from '../../../constants/abTestsConstants.ts';
import { getShowPdpEstimateInTab } from '../../helpers/getPdpEstimateSimilarSold.ts';
import { trackPricingGuidanceRangeDisplay } from 'dibs-pricing-guidance/exports/pricingGuidanceTracking.ts';
import { useShouldShowSimilarSoldItems } from '../../hooks/useShouldShowSimilarSoldItems.ts';
import { useSimilarSoldItemsTracking } from '../../hooks/useSimilarSoldItemsTracking.ts';
import { useCurrency } from '../../../shared/hooks/useCurrency.ts';

// components
import { PdpSharedEstimateValueBadge } from '../../shared/PdpSharedEstimateValueBadge/PdpSharedEstimateValueBadge.tsx';
import { PdpSharedPriceDisplay } from '../../PdpSharedPriceDisplay/PdpSharedPriceDisplay.tsx';
import { ErrorBoundary } from 'dibs-error-tools/ErrorBoundary.tsx';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense.tsx';
import { PdpSharedPromoCode } from '../../PdpSharedPromoCode/PdpSharedPromoCode.tsx';
import { PdpSharedUrgencyLazy } from '../../PdpSharedUrgency/PdpSharedUrgencyLazy.tsx';
import { SharedPerformanceTracker } from '../../../shared/SharedPerformanceTracker.tsx';
import { PdpRespSidebarBlurbs } from '../../PdpRespSidebarBlurbs/PdpRespSidebarBlurbs.tsx';
import { PdpSharedQuantitySelect } from '../../PdpSharedButtons/PdpSharedQuantitySelect.tsx';
import { PdpRespSwatchSelector } from '../../PdpRespSwatchSelector/PdpRespSwatchSelector.tsx';
import { PdpRespSidebarButtons } from '../../PdpRespSidebarButtons/PdpRespSidebarButtons.tsx';
import { PdpRespDealerChatLoader } from '../../PdpRespDealerChat/PdpRespDealerChatLoader.tsx';
import { PdpSharedDesignYourOwnPillLazy } from '../../PdpSharedDesignYourOwnPill/PdpSharedDesignYourOwnPillLazy.tsx';
import { PdpRespSharedTitle } from '../../respShared/PdpRespSharedTitle/PdpRespSharedTitle.tsx';
import { PricingGuidanceInfo } from 'dibs-pricing-guidance/exports/PricingGuidanceInfo.ts';
import { PdpSharedPageTracker } from '../../shared/PdpSharedPageTracker/PdpSharedPageTracker.tsx';
import { PdpRespIconBubblesWrapper } from '../../PdpRespIconBubbles/PdpRespIconBubblesWrapper.tsx';
import EstimatedValue from 'dibs-pricing-guidance/exports/EstimatedValue.ts';
import PdpSharedEstimateSimilarSoldRange from '../../PdpSharedEstimateSimilarSold/PdpSharedEstimateSimilarSoldRange.tsx';
import {
    PdpRespDetailTabs,
    useDetailTabsState,
} from '../../PdpRespDetailTabs/PdpRespDetailTabs.tsx';
import { PdpRespDetailAccordions } from '../../PdpRespDetailAccordions/PdpRespDetailAccordions.tsx';
import { PdpRespSimilarItemsLanding } from '../../PdpRespSimilarItems/PdpRespSimilarItemsLanding.tsx';
import { PdpRespSharedQrCodeLazy } from '../../respShared/PdpRespSharedQrCode/PdpRespSharedQrCodeLazy.tsx';
import { PdpSharedPrivateOfferBanner } from '../../PdpSharedPrivateOfferBanner/PdpSharedPrivateOfferBanner.tsx';
import { PdpRespSharedBreadcrumbs } from '../../respShared/PdpRespSharedBreadcrumbs/PdpRespSharedBreadcrumbs.tsx';
import { PdpRespSharedHeroCarousel } from '../../respShared/PdpRespSharedHeroCarousel/PdpRespSharedHeroCarousel.tsx';
import { PdpRespHeroCarousel } from '../PdpRespHeroCarousel/PdpRespHeroCarousel.tsx';
import { PdpRespSidebarSellerInfoAndContactLazy } from '../../PdpRespSidebarSellerInfo/PdpRespSidebarSellerInfoAndContactLazy.tsx';
import { ABOUT_SELLER } from '../../helpers/tabConstants.ts';
import { scrollIntoSellerModule } from '../../helpers/sellerModuleHelpers.ts';

// types
import { type PdpRespMarketplaceContainerPrimary_viewer$key as Viewer } from './__generated__/PdpRespMarketplaceContainerPrimary_viewer.graphql.ts';

// styles
import dibsCss from 'dibs-css';
import styles from './PdpRespMarketplaceContainerPrimary.scss';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

const FALLBACK_HEADER_OFFSET = 100;

const getBuyerHeaderScrollOffset = (): number => {
    const header = document.getElementById('js-header');
    return header?.offsetHeight ?? FALLBACK_HEADER_OFFSET;
};

// lazy components
const PdpRespPrintHeader = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespPrintHeader" */ '../../PdpRespPrintHeader/PdpRespPrintHeader.jsx'
        )
);

const PdpSharedItemFavorites = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedItemFavorites" */ '../../shared/PdpSharedItemFavorites/PdpSharedItemFavorites.tsx'
        )
);

const PdpSharedRestrictedMaterials = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedRestrictedMaterials" */ '../../shared/PdpSharedRestrictedMaterials/PdpSharedRestrictedMaterials.tsx'
        )
);

const PdpRespMarketplaceNotifications = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespMarketplaceNotifications" */ '../PdpRespMarketplaceNotifications/PdpRespMarketplaceNotifications.tsx'
        )
);

const PdpRespSharedPrintDetails = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespSharedPrintDetails" */ '../../respShared/PdpRespSharedPrintDetails/PdpRespSharedPrintDetails.tsx'
        )
);

const MerchandizingTool = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "MerchandizingTool" */ 'dibs-merchandizing-tools/exports/MerchandizingTool.ts'
        )
);

const viewerFragment = graphql`
    fragment PdpRespMarketplaceContainerPrimary_viewer on Viewer {
        ...PdpRespDetailTabs_viewer @skip(if: $isPdpItemModuleTestVariant)
        ...PdpRespDetailAccordions_viewer @include(if: $isPdpItemModuleTestVariant)
        ...PdpSharedPromoCode_viewer
        ...PdpRespSharedHeroCarousel_viewer
        ...PdpRespHeroCarousel_viewer
        ...PdpSharedImageGalleryModal_viewer
        ...PdpRespIconBubblesWrapper_viewer
        ...PdpSharedPageTracker_viewer @include(if: $afterDisplay)
        ...PdpRespDealerChatLoader_viewer @include(if: $afterDisplay)
        ...PdpSharedRestrictedMaterials_refactor_viewer @include(if: $afterDisplay)
        ... @include(if: $isEstimateSimilarSoldVariant) {
            ...getPdpEstimateSimilarSold_viewer @include(if: $afterDisplay)
        }
        ... @include(if: $isPdpSimilarSoldItemsModuleVariant) {
            ...PdpSharedEstimateValueBadge_viewer @include(if: $afterDisplayOrBtSsr)
        }
        ...useShouldShowSimilarSoldItems_viewer
        ...useSimilarSoldItemsTracking_viewer
        ...PdpRespSidebarSellerInfoAndContactLazy_viewer @include(if: $afterDisplay)
        ...PdpRespSidebarButtons_viewer @include(if: $afterDisplayOrBtSsr)
        itemRead(itemIds: $itemIds) {
            ... on Item {
                itemIsOnHold: isOnHold(buyerId: $userId)
                seller {
                    isAccessible
                    ...PdpRespSidebarSellerInfoAndContactLazy_seller @include(if: $afterDisplay)
                }
                displayInfo(page: pdp, buyerId: $userId) {
                    ctas {
                        showAcceptOffer
                    }
                    ...PdpSharedPromoCode_displayInfo
                    ...PdpRespSidebarBlurbs_displayInfo @include(if: $afterDisplay)
                    ...useDisplayInfo_displayInfo @include(if: $afterDisplayOrBtSsr)
                }
                skus {
                    serviceId
                    ...PdpRespSidebarBlurbs_sku @include(if: $afterDisplay)
                }
                ...PdpSharedPriceDisplay_item @include(if: $afterDisplayOrBtSsr)
                ...PdpRespDetailTabs_item @skip(if: $isPdpItemModuleTestVariant)
                ...PdpRespDetailAccordions_item @include(if: $isPdpItemModuleTestVariant)
                ...PdpRespSharedTitle_item
                ...PdpSharedDesignYourOwnPillLazy_item
                ...EstimatedValue_item @include(if: $afterDisplay)
                ...PdpRespSharedBreadcrumbs_item
                ...PdpRespIconBubblesWrapper_item
                ...PdpRespSharedHeroCarousel_item
                ...PdpRespHeroCarousel_item
                ...PdpSharedImageGalleryModal_item
                ...PdpRespSimilarItemsLanding_item
                ...PricingGuidanceInfo_item @include(if: $afterDisplay)
                ...PdpRespSwatchSelector_item @include(if: $isMultiSku)
                ...PdpRespSidebarBlurbs_item @include(if: $afterDisplay)
                ...PdpSharedItemFavorites_item @include(if: $afterDisplay)
                ...PdpRespSharedPrintDetails_item @include(if: $afterDisplay)
                ...PdpRespSidebarButtons_item @include(if: $afterDisplayOrBtSsr)
                ...PdpRespMarketplaceNotifications_item @include(if: $afterDisplay)
                ...PdpSharedQuantitySelect_item @include(if: $afterDisplayOrBtSsr)
                ...PdpRespSidebarSellerInfoAndContactLazy_item @include(if: $afterDisplay)
                ... @include(if: $isEstimateSimilarSoldVariant) {
                    ...PdpSharedEstimateSimilarSoldRange_item @include(if: $afterDisplay)
                    ...getPdpEstimateSimilarSold_item @include(if: $afterDisplay)
                }
                ...showPricingGuidance_item @include(if: $afterDisplay)
                ...pricingGuidanceTracking_item @include(if: $afterDisplay)
                ... @include(if: $isPdpSimilarSoldItemsModuleVariant) {
                    ...PdpSharedEstimateValueBadge_item @include(if: $afterDisplayOrBtSsr)
                }
                ...useShouldShowSimilarSoldItems_item
            }
        }
        user(userId: $userId) @include(if: $fetchUser) {
            hasMerchandizingPermission: hasPermission(name: "BUYER_FE_MERCH_TOOL")
            ...PdpRespDetailTabs_user @skip(if: $isPdpItemModuleTestVariant)
            ...PdpRespDetailAccordions_user @include(if: $isPdpItemModuleTestVariant)
            ...PdpSharedPromoCode_user
            ...PdpRespIconBubblesWrapper_user
            ...PdpRespSharedHeroCarousel_user
            ...PdpRespHeroCarousel_user
            ...PdpSharedImageGalleryModal_user
            ...PdpRespSimilarItemsLanding_user
            ...PdpRespSwatchSelector_user @include(if: $isMultiSku)
            ...PdpRespSidebarBlurbs_user @include(if: $afterDisplay)
            ...PdpSharedItemFavorites_user @include(if: $afterDisplay)
            ...PdpRespSharedPrintDetails_user @include(if: $afterDisplay)
            ...PdpRespSidebarButtons_user @include(if: $afterDisplayOrBtSsr)
            ...PdpRespMarketplaceNotifications_user @include(if: $afterDisplay)
            ...PdpRespSidebarSellerInfoAndContactLazy_user @include(if: $afterDisplay)
        }
        similarLandingItems: moreLikeThisItem(
            first: $similarItemsCount
            itemId: $itemId
            isTrade: $isTrade
            useML: true
        ) {
            ...PdpRespSimilarItemsLanding_similarItems
        }
        ... @include(if: $afterDisplay) {
            buyerUrgencyInfo(itemId: $itemId, userId: $userId) {
                ...PdpSharedUrgencyLazy_buyerUrgencyInfo
                itemUrgency {
                    hasUrgencyInfo
                }
            }
        }
    }
`;

export const PdpRespMarketplaceContainerPrimary: FC<{ viewer: Viewer }> = ({
    viewer: viewerRef,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const detailsSectionRef = useRef<HTMLDivElement>(null);
    const primaryContainerBottomRef = useRef<HTMLDivElement>(null);
    const scrollToCustomizationRef = useRef<HTMLDivElement>(null);
    const aboutSellerPanelRef = useRef<HTMLDivElement>(null);
    const detailTabsState = useDetailTabsState();
    const [, setActiveTab] = detailTabsState;
    const [requestedAccordionSection, setRequestedAccordionSection] = useState<string | null>(null);
    const [itemQuantity, setItemQuantity] = useState<number | null>(null);
    const { currency } = useCurrency();
    const { onSkuChange, selectedSkuId, printWithPrice, relayVariables } =
        usePdpRespMarketplaceLayoutPrimaryContext();
    const { onPdpRespEstimateValueBadgeClick } = usePdpSharedSimilarSoldItemsContext();
    const { trackSimilarSoldQualifyingPdpLoad, trackEstimateValueBadgeImpression } =
        useSimilarSoldItemsTracking({ viewer });
    const {
        isTablet,
        isPdpRespPreviewPage,
        isPdpEstimateSimilarSoldTestVariant,
        isItemFavoritingFeb2026Variant,
        isPdpSellerModuleTestVariant,
        isPdpItemModuleTestVariant,
    } = useServerVarsContext();
    const { userId, itemId, isTrade, isMultiSku, afterDisplay, afterDisplayOrBtSsr } =
        relayVariables?.variables || {};
    const { itemRead, user = null, similarLandingItems, buyerUrgencyInfo } = viewer;
    const item = itemRead?.[0] || null;
    const { hasUrgencyInfo } = buyerUrgencyInfo?.itemUrgency || {};

    const selectedSku = item?.skus?.find(sku => sku?.serviceId === selectedSkuId) || null;

    const showPdpEstimateInTab =
        afterDisplay &&
        isPdpEstimateSimilarSoldTestVariant &&
        getShowPdpEstimateInTab({
            viewer: viewer || null,
            item,
            currency,
            isPdpEstimateSimilarSoldTestVariant: !!isPdpEstimateSimilarSoldTestVariant,
        });

    const showPricingGuidance = afterDisplay && showPricingGuidanceHelper({ item, currency });

    const {
        shouldShowEstimateValueBadge,
        areEligibleConditionsMet,
        isBelowMinPriceEstimate,
        isBelowOrEqualToMaxPriceEstimate,
    } = useShouldShowSimilarSoldItems({
        viewer,
        item,
        currency,
        afterDisplay,
    });

    const navigateToSellerSection = useCallback(() => {
        if (isPdpSellerModuleTestVariant) {
            scrollIntoSellerModule({
                primaryContainerBottomRef,
            });
        } else if (isPdpItemModuleTestVariant) {
            setRequestedAccordionSection(ABOUT_SELLER);
        } else {
            setActiveTab(ABOUT_SELLER);
            requestAnimationFrame(() => {
                const el = aboutSellerPanelRef.current;
                if (el) {
                    scrollIntoView(el, {
                        time: 500,
                        align: { top: 0, topOffset: getBuyerHeaderScrollOffset() },
                    });
                }
            });
        }
    }, [isPdpSellerModuleTestVariant, isPdpItemModuleTestVariant, setActiveTab]);

    const handleRequestedAccordionSectionHandled = useCallback(() => {
        setRequestedAccordionSection(null);
        requestAnimationFrame(() => {
            const el = aboutSellerPanelRef.current;
            if (el) {
                scrollIntoView(el, {
                    time: 500,
                    align: { top: 0, topOffset: getBuyerHeaderScrollOffset() },
                });
            }
        });
    }, []);

    useEffect(() => {
        if (areEligibleConditionsMet) {
            trackSimilarSoldQualifyingPdpLoad({
                isBelowMinPriceEstimate,
                isBelowOrEqualToMaxPriceEstimate,
            });
            trackAbTestV2Variant(PDP_SIMILAR_SOLD_ITEMS_MODULE_TEST_NAME);
        }
    }, [
        areEligibleConditionsMet,
        isBelowMinPriceEstimate,
        isBelowOrEqualToMaxPriceEstimate,
        trackSimilarSoldQualifyingPdpLoad,
    ]);

    useEffect(() => {
        if (showPricingGuidance) {
            trackAbTestV2Variant(PDP_ESTIMATE_SIMILAR_SOLD_TEST_NAME);
        }
    }, [showPricingGuidance]);

    useEffect(() => {
        if (afterDisplay && item) {
            trackPricingGuidanceRangeDisplay({ item });
        }
    }, [afterDisplay, item]);

    useEffect(() => {
        trackAbTestV2Variant(PDP_ITEM_MODULE_TEST_NAME);
    }, []);

    if (!item) {
        return null;
    }

    const { itemIsOnHold, seller, displayInfo } = item;
    const { showAcceptOffer } = displayInfo?.ctas || {};
    const isSellerAccessible = !!seller?.isAccessible;
    const includePromoCode = !itemIsOnHold && isSellerAccessible;
    const showFavoriteInImageOverlay = afterDisplay && !isTablet;

    return (
        <>
            {afterDisplay && <PdpSharedPageTracker viewer={viewer} selectedSkuId={selectedSkuId} />}
            {afterDisplay && (
                <ClientSuspense fallback={null}>
                    <div className={styles.printShow}>
                        <PdpRespPrintHeader itemId={itemId} />
                    </div>
                </ClientSuspense>
            )}
            <div className={styles.printHide}>
                <PdpRespSimilarItemsLanding
                    item={item}
                    user={user}
                    similarItems={similarLandingItems}
                    scrollToRef={primaryContainerBottomRef}
                />
                <PdpRespSharedBreadcrumbs item={item} />
            </div>
            <div
                className={cn(
                    'rowFlex',
                    dibsCss.relative,
                    !isTablet &&
                        cn(
                            styles.pdpLargerHeroRow,
                            dibsCss.maxW1440px,
                            dibsCss.m0,
                            dibsCss.ptSmall,
                            dibsCss.px0,
                            dibsCss.pb0
                        )
                )}
                data-tn="pdp-main-container"
            >
                {/* Image Carousel */}
                <div className={cn('colXs12 colLg7 colXl8', dibsCss.ptSmall, styles.topContainer)}>
                    {!isTablet ? (
                        <PdpRespHeroCarousel
                            item={item}
                            user={user}
                            viewer={viewer}
                            currency={currency}
                            afterDisplay={!!afterDisplay}
                            selectedSkuId={selectedSkuId}
                            disableModals={isPdpRespPreviewPage}
                            imageOverlay={
                                showFavoriteInImageOverlay ? (
                                    <ClientSuspense fallback={null}>
                                        <PdpSharedItemFavorites
                                            item={item}
                                            user={user}
                                            showHeartCount
                                            wrapperClass={dibsCss.contents}
                                            heartIconWrapperClass={dibsCss.mt0}
                                            folderIconWrapperClass={dibsCss.mt0}
                                            animateHeartPulse={false}
                                            showNudge={isItemFavoritingFeb2026Variant}
                                            theme="circle-frosted-large"
                                        />
                                    </ClientSuspense>
                                ) : undefined
                            }
                        >
                            <PdpRespIconBubblesWrapper
                                item={item}
                                user={user}
                                viewer={viewer}
                                skuId={selectedSkuId}
                                relayVariables={{
                                    isTrade: !!isTrade,
                                }}
                            />
                        </PdpRespHeroCarousel>
                    ) : (
                        <PdpRespSharedHeroCarousel
                            item={item}
                            user={user}
                            viewer={viewer}
                            currency={currency}
                            afterDisplay={!!afterDisplay}
                            selectedSkuId={selectedSkuId}
                            size={isTablet ? 'medium' : 'large'}
                            disableModals={isPdpRespPreviewPage}
                            enableImageGalleryModal
                        >
                            <PdpRespIconBubblesWrapper
                                item={item}
                                user={user}
                                viewer={viewer}
                                skuId={selectedSkuId}
                                relayVariables={{
                                    isTrade: !!isTrade,
                                }}
                            />
                        </PdpRespSharedHeroCarousel>
                    )}
                </div>
                {/* Sidebar */}
                <div
                    className={cn(
                        'colXs12 colLg5 colXl4',
                        styles.topContainer,
                        styles.sidebarContainer,
                        dibsCss.ptSmall,
                        dibsCss.pxLarge,
                        dibsCss.pb0
                    )}
                >
                    <div className={'rowFlex'}>
                        <div
                            className={`colXs12 ${
                                showFavoriteInImageOverlay ? 'colLg12' : 'colLg10'
                            }`}
                        >
                            <div className={styles.urgencyPlaceholder}>
                                {afterDisplay && buyerUrgencyInfo && hasUrgencyInfo && (
                                    <PdpSharedUrgencyLazy buyerUrgencyInfo={buyerUrgencyInfo} />
                                )}
                                {afterDisplay && !hasUrgencyInfo && (
                                    <PdpSharedDesignYourOwnPillLazy
                                        item={item}
                                        scrollToCustomizationRef={scrollToCustomizationRef}
                                    />
                                )}
                            </div>
                            <PdpRespSharedTitle item={item} />
                        </div>
                        {/* Favorite / Folder */}
                        {!showFavoriteInImageOverlay && (
                            <div className={cn('colXs2', styles.printHide)}>
                                {afterDisplay && (
                                    <ClientSuspense fallback={null}>
                                        <PdpSharedItemFavorites
                                            item={item}
                                            user={user}
                                            showHeartCount
                                            wrapperClass={cn(
                                                dibsCss.mt0,
                                                dibsCss.top0,
                                                dibsCss.absolute,
                                                dibsCss.contents,
                                                dibsCss.rightXsmall
                                            )}
                                            heartIconWrapperClass={dibsCss.mt0}
                                        />
                                    </ClientSuspense>
                                )}
                            </div>
                        )}

                        <div className={cn(dibsCss.wFull, dibsCss.pxXsmall)}>
                            {afterDisplay && showAcceptOffer && <PdpSharedPrivateOfferBanner />}
                            <div className={cn({ [styles.printHide]: !printWithPrice })}>
                                {afterDisplay && (
                                    <SharedPerformanceTracker
                                        datadogTimer={{ name: 'pdp_time_to_price' }}
                                        measureFromPageInit={{
                                            subGroup: 'resp/price-box',
                                            monitoringGroup: LABEL_VALUE_MONITORING_GROUP_PDP,
                                        }}
                                    />
                                )}
                                {afterDisplayOrBtSsr && (
                                    <>
                                        <PdpSharedPriceDisplay
                                            item={item}
                                            currency={currency}
                                            selectedSkuId={selectedSkuId}
                                            rightDecorator={
                                                shouldShowEstimateValueBadge ? (
                                                    <PdpSharedEstimateValueBadge
                                                        viewer={viewer}
                                                        item={item}
                                                        currency={currency}
                                                        onClick={onPdpRespEstimateValueBadgeClick}
                                                    />
                                                ) : undefined
                                            }
                                        />
                                        {areEligibleConditionsMet &&
                                            isBelowOrEqualToMaxPriceEstimate && (
                                                <VisibilityTracker
                                                    onVisibilityChange={({ isVisible }) => {
                                                        if (isVisible) {
                                                            trackEstimateValueBadgeImpression({
                                                                belowEstimate:
                                                                    isBelowMinPriceEstimate,
                                                            });
                                                        }
                                                    }}
                                                />
                                            )}
                                    </>
                                )}
                                {afterDisplay && isPdpEstimateSimilarSoldTestVariant && (
                                    <PdpSharedEstimateSimilarSoldRange
                                        item={item}
                                        currency={currency}
                                        estimatePricingRef={
                                            showPdpEstimateInTab
                                                ? scrollToCustomizationRef
                                                : primaryContainerBottomRef
                                        }
                                    />
                                )}
                                {afterDisplay && !isPdpEstimateSimilarSoldTestVariant && (
                                    <EstimatedValue
                                        item={item}
                                        currency={currency}
                                        infoContent={
                                            <PricingGuidanceInfo
                                                item={item}
                                                iconClassName={dibsCss.fillSatan}
                                                trigger="siderail"
                                            />
                                        }
                                        textClassName={dibsCss.sassyFontBodyXSmallHeavy}
                                    />
                                )}
                                {includePromoCode && (
                                    <PdpSharedPromoCode
                                        displayInfo={displayInfo}
                                        itemId={itemId}
                                        viewer={viewer}
                                        user={user}
                                        userId={userId}
                                        currency={currency}
                                        excludeShippingPromos
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    {isMultiSku && (
                        <PdpRespSwatchSelector
                            item={item}
                            user={user}
                            currency={currency}
                            isClient={afterDisplay}
                            showSwatchDescriptionOnHover
                            selectedSkuId={selectedSkuId}
                            onClick={(skuId: string) => onSkuChange(skuId)}
                        />
                    )}
                    {/* Item Ctas */}
                    {afterDisplayOrBtSsr && (
                        <div
                            className={cn(
                                dibsCss.mtSmall,
                                dibsCss.mbXxsmall,
                                dibsCss.mx0,
                                styles.printHide
                            )}
                        >
                            <PdpSharedQuantitySelect
                                item={item}
                                selectedSkuId={selectedSkuId}
                                setItemQuantity={setItemQuantity}
                            >
                                {quantitySelected => (
                                    <PdpRespSidebarButtons
                                        user={user}
                                        item={item}
                                        viewer={viewer}
                                        relayVariables={{
                                            itemId,
                                            userId,
                                            isMultiSku: isMultiSku || undefined,
                                        }}
                                        displayInfo={displayInfo}
                                        skuId={selectedSkuId || undefined}
                                        quantitySelected={quantitySelected || undefined}
                                        currency={currency}
                                    />
                                )}
                            </PdpSharedQuantitySelect>
                        </div>
                    )}
                    {/* Restricted Materials */}
                    {afterDisplay && (
                        <ClientSuspense fallback={null}>
                            <PdpSharedRestrictedMaterials viewer={viewer} />
                        </ClientSuspense>
                    )}
                    {/* Blurbs */}
                    {afterDisplay && isSellerAccessible && (
                        <>
                            <PdpRespSidebarBlurbs
                                item={item}
                                user={user}
                                currency={currency}
                                displayInfo={displayInfo}
                                sku={selectedSku || null}
                                itemQuantity={itemQuantity || undefined}
                            />
                            {afterDisplay && (
                                <div
                                    className={cn(
                                        styles.printHide,
                                        dibsCss.ptSmall,
                                        dibsCss.borderT,
                                        dibsCss.borderTSolid,
                                        dibsCss.borderTDolphin
                                    )}
                                >
                                    {/* Questions about this piece? / Contact Dealer */}
                                    <PdpRespSidebarSellerInfoAndContactLazy
                                        viewer={viewer}
                                        user={user}
                                        item={item}
                                        seller={seller}
                                        currency={currency}
                                        isClient={afterDisplay}
                                        selectedSkuId={selectedSkuId || ''}
                                        itemQuantity={itemQuantity || undefined}
                                        onNavigateToSellerSection={navigateToSellerSection}
                                    />
                                </div>
                            )}
                        </>
                    )}
                    <PdpRespSharedQrCodeLazy itemId={itemId} className={styles.printShow} />
                </div>
                {user?.hasMerchandizingPermission && userId && (
                    <ClientSuspense fallback={null}>
                        <div data-dibs-merchandizing-tool-item={itemId} />
                        <MerchandizingTool itemIds={[itemId]} userId={userId} />
                    </ClientSuspense>
                )}
            </div>
            <div ref={scrollToCustomizationRef} />
            <div
                ref={detailsSectionRef}
                className={cn(dibsCss.mtMedium, dibsCss.mbLarge, styles.details, styles.printHide)}
            >
                {isPdpItemModuleTestVariant ? (
                    <PdpRespDetailAccordions
                        item={item}
                        user={user}
                        viewer={viewer}
                        currency={currency}
                        itemQuantity={itemQuantity}
                        aboutSellerPanelRef={aboutSellerPanelRef}
                        selectedSkuId={selectedSkuId}
                        requestedOpenSection={requestedAccordionSection}
                        onRequestedOpenSectionHandled={handleRequestedAccordionSectionHandled}
                    />
                ) : (
                    <PdpRespDetailTabs
                        item={item}
                        user={user}
                        viewer={viewer}
                        currency={currency}
                        itemQuantity={itemQuantity}
                        tabsState={detailTabsState}
                        aboutSellerPanelRef={aboutSellerPanelRef}
                        selectedSkuId={selectedSkuId}
                    />
                )}
            </div>
            {afterDisplay && <PdpRespDealerChatLoader viewer={viewer} currency={currency} />}
            {afterDisplay && (
                <div className={styles.printShow}>
                    <ClientSuspense fallback={null}>
                        <PdpRespSharedPrintDetails
                            item={item}
                            user={user}
                            currency={currency}
                            selectedSkuId={selectedSkuId}
                        />
                    </ClientSuspense>
                </div>
            )}
            <div ref={primaryContainerBottomRef} />

            {/* page notifications */}
            {afterDisplay && (
                <ErrorBoundary fallback={() => null}>
                    <ClientSuspense fallback={null}>
                        <PdpRespMarketplaceNotifications item={item} user={user} />
                    </ClientSuspense>
                </ErrorBoundary>
            )}
        </>
    );
};
